<template lang="pug">
.c-glows-page
  //- Header Section
  section.c-glows-page__header
    .c-glows-page__header-content
      h1.c-glows-page__heading Glows
      p.c-glows-page__subtitle Manage glow campaigns and their schedules
    
    .c-glows-page__header-actions
      el-button.c-glows-action-btn(@click="$router.push('/manage-content/media/glows/create')")
        fai(type="fal" icon="plus-circle")
        span Create Glow

  //- Content Section
  .c-glows-content
    //- Table Section
    section.c-glows-table-section
      CmsListToolbar(
        :tabs="viewTabs"
        :active-tab="state.currentView"
        :tabs-disabled="loading"
        :chips="orgChips"
        v-model="state.searchTerm"
        search-placeholder="Filter glows"
        :sort-value="state.sortOption"
        :sort-options="sortOptions"
        :selected-count="selected.length"
        @tab-change="headerFilter"
        @search="searchItems"
        @sort="handleSortChange"
        @remove-chip="clearOrgFilter"
        @clear-selection="tableRef?.clearSelection()"
      )
        template(#bulk)
          el-select.c-glows-bulk-select(
            v-model="setAs"
            value-key="value"
            placeholder="Set as"
            @change="bulkUpdate($event.value)"
          )
            el-option(
              v-for="item in setAsOption"
              :key="item.value"
              :label="item.label"
              :value="item"
            )

      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--title
            .c-skeleton-cell.c-skeleton-cell--status
            .c-skeleton-cell.c-skeleton-cell--org
            .c-skeleton-cell.c-skeleton-cell--cta
            .c-skeleton-cell.c-skeleton-cell--link
            .c-skeleton-cell.c-skeleton-cell--date
      
      //- Table
      el-table(
        v-else
        ref="tableRef"
        :data="items.items"
        @selection-change="onCheckboxChange"
        @row-click="handleRowClick"
        style="width: 100%"
      ).clickable-rows
        el-table-column(type="selection" width="55")

        el-table-column(label="Title" min-width="150")
          template(#default="scope")
            .title-cell
              .color-indicator(:style="{ backgroundColor: scope.row.glowColor }")
              .title-content
                .title-text {{ scope.row.title }}
                .description-text(v-if="scope.row.description") {{ scope.row.description }}

        el-table-column(label="Status" width="150")
          template(#default="scope")
            .status-badge(:style="getStatusStyle(scope.row.status)")
              | {{ scope.row.status ? scope.row.status.charAt(0) + scope.row.status.slice(1).toLowerCase() : 'Draft' }}

        el-table-column(label="Organization" width="150")
          template(#default="scope")
            .org-cell(v-if="scope.row.organization")
              img.org-avatar(
                v-if="scope.row.organization.avatar"
                :src="scope.row.organization.avatar"
                alt="Organization"
              )
              .org-avatar.no-image(v-else) No image
              span.org-name {{ scope.row.organization.name }}
            .org-cell(v-else)
              span.text-muted N/A

        el-table-column(label="CTA" width="250")
          template(#default="scope")
            .cta-cell(v-if="scope.row.ctaOrganization")
              img.cta-avatar(
                v-if="scope.row.ctaOrganization.avatar"
                :src="scope.row.ctaOrganization.avatar"
                alt="Organization"
              )
              .cta-avatar.no-image(v-else) No image
              span.cta-title {{ scope.row.ctaOrganization.name }}
            .cta-cell(v-else-if="scope.row.ctaSticker")
              img.cta-avatar(
                v-if="scope.row.ctaSticker.url"
                :src="scope.row.ctaSticker.url"
                alt="Sticker"
              )
              .cta-avatar.no-image(v-else) No image
              span.cta-title {{ scope.row.ctaSticker.title }}
            .cta-cell(v-else)
              span.cta-standalone No CTA

        el-table-column(label="Share Link" width="200")
          template(#default="scope")
            .share-link-cell(v-if="scope.row")
              span.share-link-text {{ getGlowShortUrl(scope.row) }}
              fai(
                type="fal"
                icon="copy"
                @click.stop="copyShareLink(scope.row)"
              ).copy-icon.size-16
            .share-link-cell(v-else)
              span.text-muted.fs-12 N/A

        el-table-column(label="Start Date" width="250")
          template(#default="scope")
            div {{ formatRelativeDateTime(scope.row.glowStartDate) }}

      //- Pagination and Page Size
      .c-glows-pagination(v-if="items.items && items.items.length > 0")
        .c-glows-pagination__page-size
          span.c-glows-pagination__page-size-label Items per page:
          el-select.c-glows-pagination__page-size-select(
            v-model="state.pageSize"
            @change="handleSizeChange"
            size="small"
          )
            el-option(
              v-for="size in [20, 30, 50]"
              :key="size"
              :label="size"
              :value="size"
            )

        .c-glows-pagination__controls
          el-button.c-glows-pagination__button(
            @click="handleCurrentChange(state.currentPage - 1)"
            :disabled="state.currentPage === 1 || loading"
          ) Prev
          .c-glows-pagination__info
            span.c-glows-pagination__page-number Page {{ state.currentPage }}
            span.c-glows-pagination__item-count Showing {{ ((state.currentPage - 1) * state.pageSize) + 1 }}-{{ ((state.currentPage - 1) * state.pageSize) + items.items.length }}
          el-button.c-glows-pagination__button(
            @click="handleCurrentChange(state.currentPage + 1)"
            :disabled="!state.hasMoreItems || loading"
          ) Next

  //- Error State
  .c-glows-error(v-if="state.apiError && !loading")
    .c-glows-error__icon
      fai(type="fal" icon="exclamation-triangle").size-48
    h3.c-glows-error__title Error loading glows
    p.c-glows-error__text {{ state.apiError }}
    el-button.c-glows-error__button(
      @click="getItems"
      type="primary"
    ) Retry
</template>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref, computed, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import CmsListToolbar from '@/components/CmsListToolbar.vue';
import glowShared from './shared';

export default defineComponent({
  name: 'ManageContent_Media_Glows',
  components: { CmsListToolbar },
  setup() {
    const {
      getGlows,
      getPastGlows,
      getUpcomingGlows,
      updateGlowStatus,
      loading,
      dateAndTime,
      formatRelativeDateTime,
    } = glowShared();
    
    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const items = reactive({ items: [] });
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);
    const tableRef = ref();
    const viewTabs = [
      { label: 'All', value: 'ALL' },
      { label: 'Upcoming', value: 'UPCOMING' },
      { label: 'Past', value: 'PAST' },
      { label: 'Draft', value: 'DRAFT' },
      { label: 'Archived', value: 'ARCHIVED' },
    ];
    // Org admins are always scoped to their own org, so there is nothing to clear
    const orgChips = computed(() => (
      selectedOrganization.value?.id && !store.getters['users/isOrgAdmin']
        ? [{ key: 'org', label: 'Org', value: selectedOrganization.value.name }]
        : []));

    const sortOptions = [
      { label: 'Start Date (Soonest)', value: { field: 'GlowStartDate', asc: true } },
      { label: 'Start Date (Latest)', value: { field: 'GlowStartDate', asc: false } },
      { label: 'Most Recent', value: { field: 'CreatedAt', asc: false } },
      { label: 'Oldest First', value: { field: 'CreatedAt', asc: true } },
      { label: 'Alphabetical (A-Z)', value: { field: 'Title', asc: true } },
      { label: 'Alphabetical (Z-A)', value: { field: 'Title', asc: false } },
    ];

    const state = reactive<{
      currentView: string;
      searchTerm: string;
      pageSize: number;
      currentPage: number;
      sortBy: { field: string; asc: boolean };
      sortOption: any;
      activeFilters: string[];
      hasMoreItems: boolean;
      apiError: string | null;
    }>({
      currentView: 'UPCOMING',
      searchTerm: '',
      pageSize: 20,
      currentPage: 1,
      sortBy: { field: 'GlowStartDate', asc: true },
      sortOption: sortOptions[0],
      activeFilters: ['UPCOMING'],
      hasMoreItems: true,
      apiError: null,
    });

    const selected = ref([]);

    const setAsOption = [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Draft', value: 'DRAFT' },
      { label: 'Archived', value: 'ARCHIVED' },
    ];
    const setAs = ref('');

    async function getItems() {
      try {
        loading.value = true;
        state.apiError = null;

        const skip = (state.currentPage - 1) * state.pageSize;
        const sortBy = state.sortBy.field;
        const sortAsc = state.sortBy.asc;

        // Get organization ID for filtering
        const organizationId = selectedOrganization.value ? selectedOrganization.value.id : '';

        // Determine which query to use based on current view
        let fetchFunction = getGlows;
        let status: string | null = null;

        if (state.currentView === 'UPCOMING') {
          fetchFunction = getUpcomingGlows;
        } else if (state.currentView === 'PAST') {
          fetchFunction = getPastGlows;
        } else if (state.currentView !== 'ALL') {
          // For DRAFT and ARCHIVED, use status filter with getGlows
          status = state.currentView;
        }

        const result = await fetchFunction(
          state.pageSize,
          skip,
          state.searchTerm,
          sortBy,
          sortAsc,
          status,
          organizationId,
        );

        if (!result) {
          items.items = [];
          state.hasMoreItems = false;
          state.apiError = 'API returned undefined or null result';
          return;
        }

        items.items = result;

        if (items.items.length === state.pageSize) {
          const nextPageResult = await fetchFunction(
            1,
            skip + state.pageSize,
            state.searchTerm,
            sortBy,
            sortAsc,
            status,
            organizationId,
          );

          state.hasMoreItems = nextPageResult && nextPageResult.length > 0;
        } else {
          state.hasMoreItems = false;
        }
      } catch (err) {
        console.error('Error fetching glows:', err);
        state.apiError = 'Error loading glows. Please try again later.';
      } finally {
        loading.value = false;
      }
    }

    function searchItems() {
      state.currentPage = 1;
      getItems();
    }

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function handleRowClick(row) {
      router.push({
        path: `/manage-content/media/glows/edit/${row.id}`,
        query: {
          returnTab: state.currentView,
          // Pass the glow's org ID so edit page can load even without menu selection
          glowOrgId: row.organization?.id,
        },
      });
    }

    async function bulkUpdate(type) {
      try {
        loading.value = true;

        // Use selected organization from menu, or fall back to glow's organization
        const orgId = selectedOrganization.value?.id;

        // Update each selected glow's status using its own organization ID if no org selected
        const updatePromises = selected.value.map((element: any) => {
          const glowOrgId = orgId || element.organization?.id;
          if (!glowOrgId) {
            throw new Error(`No organization ID found for glow: ${element.title}`);
          }
          return updateGlowStatus(element.id, type, glowOrgId);
        });

        await Promise.all(updatePromises);

        ElMessage.success(`Successfully updated ${selected.value.length} glow(s) to ${type}`);

        // Refresh the list
        await getItems();

        setAs.value = '';
        selected.value = [];
      } catch (error) {
        console.error('Error updating glow statuses:', error);
        ElMessage.error('Failed to update glow statuses. Please try again.');
      } finally {
        loading.value = false;
      }
    }

    function handleSortChange(selectedSort) {
      if (selectedSort && selectedSort.value) {
        state.sortBy = selectedSort.value;
        state.sortOption = selectedSort;
        state.currentPage = 1;
        getItems();
      }
    }

    function handleSizeChange(newSize) {
      state.pageSize = newSize;
      state.currentPage = 1;
      getItems();
    }

    function handleCurrentChange(newPage) {
      state.currentPage = newPage;
      getItems();
    }

    function headerFilter(filterBy) {
      state.currentView = filterBy;
      state.activeFilters = [filterBy];
      state.currentPage = 1;
      getItems();
    }

    watch(
      () => selectedOrganization.value,
      (newOrg, oldOrg) => {
        if (newOrg?.id !== oldOrg?.id) {
          state.currentPage = 1;
          getItems();
        }
      },
    );

    function clearOrgFilter() {
      store.dispatch('organizations/SET_SELECTED_ORGANIZATION', null);
      state.currentPage = 1;
      getItems();
    }

    function getStatusStyle(status: string) {
      const statusColors: { [key: string]: string } = {
        ACTIVE: '#8DD8CD',
        DRAFT: '#96C9EF',
        ARCHIVED: '#EBEBEB',
      };
      const backgroundColor = statusColors[status] || statusColors.DRAFT;
      return {
        backgroundColor,
        color: status === 'ARCHIVED' ? '#666' : '#333',
      };
    }

    function getGlowShortUrl(glow: any): string {
      if (!glow) return '';
      const baseUrl = import.meta.env.VITE_WEB_BASE_URL || '';
      const identifier = glow.slug || glow.id;
      return `${baseUrl}/web/glow/${identifier}`;
    }

    async function copyShareLink(glow: any) {
      try {
        const url = getGlowShortUrl(glow);

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          ElMessage.success('Share link copied to clipboard!');
        } else {
          // Fallback for older browsers or when clipboard API is not available
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            document.execCommand('copy');
            ElMessage.success('Share link copied to clipboard!');
          } catch (err) {
            console.error('Fallback copy failed:', err);
            ElMessage.error('Failed to copy share link');
          } finally {
            document.body.removeChild(textArea);
          }
        }
      } catch (error) {
        console.error('Failed to copy share link:', error);
        ElMessage.error('Failed to copy share link');
      }
    }

    onMounted(async () => {
      // Check if returning from edit page with a specific tab
      const returnTab = route.query.returnTab as string;
      if (returnTab && ['ALL', 'UPCOMING', 'PAST', 'DRAFT', 'ARCHIVED'].includes(returnTab)) {
        state.currentView = returnTab;
        state.activeFilters = [returnTab];
      }

      await getItems();
    });

    return {
      items,
      loading,
      state,
      selected,
      sortOptions,
      setAsOption,
      setAs,
      selectedOrganization,
      onCheckboxChange,
      handleRowClick,
      bulkUpdate,
      handleSortChange,
      handleSizeChange,
      handleCurrentChange,
      searchItems,
      tableRef,
      viewTabs,
      orgChips,
      headerFilter,
      dateAndTime,
      formatRelativeDateTime,
      getStatusStyle,
      getItems,
      getGlowShortUrl,
      copyShareLink,
      clearOrgFilter,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-glows-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

}

// Action button styles
.c-glows-action-btn {
  height: 36px;
  font-size: 14px;
  border-radius: 8px;
  background-color: white !important;
  border: 1.5px solid $color-purple-text !important;
  color: $color-purple-text !important;

  &:hover,
  &:focus {
    background-color: rgba($color-purple-text, 0.08) !important;
    border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    color: color.adjust($color-purple-text, $lightness: -8%) !important;
  }

  :deep(span) {
    font-weight: 500;
  }

  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

.c-glows-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.c-glows-bulk-select {
  width: 180px;
}

// Table Section
.c-glows-table-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    tbody tr {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Pagination
.c-glows-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid $gray-100;
  flex-wrap: wrap;
  gap: 1rem;

  &__page-size {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__page-size-label {
    font-weight: 500;
    color: $gray-700;
    font-size: 0.875rem;
  }

  &__page-size-select {
    width: 80px;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  &__button {
    border-radius: 0.375rem;
    font-weight: 500;
    transition: all 0.15s ease;
    background-color: transparent;
    border: 1px solid $gray-300;
    color: $gray-700;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;

    &:hover:not(:disabled) {
      background-color: transparent;
      border-color: $gray-500;
      color: $gray-900;
    }

    &:disabled {
      border-color: $gray-300;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    min-width: 140px;
  }

  &__page-number {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-900;
  }

  &__item-count {
    font-size: 0.75rem;
    color: $gray-600;
  }
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--checkbox {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &--title {
    width: 180px;
    flex-shrink: 0;
  }

  &--status {
    width: 100px;
    flex-shrink: 0;
  }

  &--org {
    width: 120px;
    flex-shrink: 0;
  }

  &--cta {
    width: 150px;
    flex-shrink: 0;
  }

  &--link {
    width: 140px;
    flex-shrink: 0;
  }

  &--date {
    width: 150px;
    flex-shrink: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Table Cell Styles
.title-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-indicator {
  width: 30px;
  min-width: 30px;
  height: 40px;
  border-radius: 4px;
}

.title-content {
  flex: 1;
  min-width: 0;
}

.title-text {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
}

.description-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.org-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.org-avatar {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;

  &.no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f7fa;
    color: #909399;
    font-size: 9px;
    text-align: center;
    line-height: 1.2;
  }
}

.org-name {
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cta-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cta-avatar {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;

  &.no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f7fa;
    color: #909399;
    font-size: 9px;
    text-align: center;
    line-height: 1.2;
  }
}

.cta-title {
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cta-standalone {
  font-size: 14px;
  color: #666;
  font-style: italic;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  min-width: 70px;
}

.share-link-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.share-link-text {
  font-size: 12px;
  color: #1fbeaf;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.copy-icon {
  color: #909399;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: color 0.3s;

  &:hover {
    color: #1fbeaf;
  }
}

// Error State
.c-glows-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  &__icon {
    color: #f56c6c;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0 0 1.5rem 0;
  }

  &__button {
    background-color: #f56c6c !important;
    border-color: #f56c6c !important;
    color: white !important;

    &:hover {
      background-color: #e45656 !important;
      border-color: #e45656 !important;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .c-glows-page {
    padding: 1.5rem;

    &__heading {
      font-size: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }
  }

  .c-glows-pagination {
    flex-direction: column;
    align-items: stretch;

    &__controls {
      flex-direction: column;
      gap: 1rem;
    }

    &__button {
      width: 100%;
    }

    &__info {
      order: -1;
    }
  }
}
</style>
